import React, {useState, useCallback} from 'react';
import update from 'immutability-helper';
import { useTable } from 'react-table'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { DragHandle } from '../../common/DragNDrop.jsx';
import Backend from 'react-dnd-html5-backend'
import { Button } from '../../bootstrap/index.jsx'

const randomTempId = () => Math.round(Math.random() * 1000000)

const EditableList = ({ columns, data, defaultRow, onSaveRowOrder, cellMethods, draggable = true, addRowLabel = '', report_type, addButtonHiddenOneRow = false }) => {
  const [records, setRecords] = useState(data || [])
  const buttonDisable = report_type === 'success_story' && Object.values(records).filter(f => !!f).length >= 1;

  const getRowId = useCallback(row => {
    return row.id
  }, [])

  const updateCell = (rowIndex, column, value) => {
    // Support single-level objects as values. Use merge to update
    const updateMethod = Object.keys(value).length > 0 && typeof value != 'string' ? '$merge' : '$set'
      setRecords(
        update(records, {
          [rowIndex]: { [column]: {[updateMethod]: value} }
        })
      )
  }

  const updateRow = (rowIndex, value) => {
    setRecords(
      update(records, {
        [rowIndex]: {$set: value}
      }
    ))
  }

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRecord = records[dragIndex]
    setRecords(
      update(records, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      })
    )
  }

  const removeRow = (rowIndex) => {
    setRecords(
      update(records, {
        $unset: [rowIndex]
      })
    )
  }

  const discardRow = (rowIndex) => {

    if (typeof records[rowIndex].id == 'string' && records[rowIndex].id.match(/new\_/)){
      setRecords(
        update(records, {
          $unset: [rowIndex]
        })
      )
    } else {
      setRecords(
        update(records, {
          [rowIndex]: {$set: records[rowIndex].originalRow} 
        })
      )
    }
  }

  const handleAddRow = (e) => {
    setRecords(
      update(records, { 
        $push: [
          {id: 'new_' + randomTempId(), new: true, ...defaultRow, editActive: true}
        ]
      })
    )
  }

  const onMoveRowFinished = () => {
    onSaveRowOrder(records);
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    data: records,
    columns,
    getRowId,
    updateCell,
    discardRow,
    removeRow,
    updateRow,
    ...cellMethods
  })

  
  return <>
    <DndProvider backend={Backend}>
      <table {...getTableProps()} className="table table-striped dragHandle">
        <thead>
          {headerGroups.map(headerGroup => {
            const {key, ...rest} = headerGroup.getHeaderGroupProps();
            return (
            <tr key={key} {...rest}>
              {draggable && <th></th>}
              {headerGroup.headers.map(column => {
                const {key, ...rest} = column.getHeaderProps();
                return <th key={key} {...rest}>{column.render('Header')}</th>
              })}
            </tr>
          )})}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(
            (row, index) =>{
              prepareRow(row);
              const {key, ...rest} = row.getRowProps();
              return <TableRow
                  index={index}
                  row={row}
                  moveRow={moveRow}
                  onMoveRowFinished={onMoveRowFinished}
                  draggable={draggable}
                  key={key}
                  {...rest}
                />
            }
          )}
        </tbody>
      </table>
    </DndProvider>
    {(!addButtonHiddenOneRow || rows.length < 1) // For approval phase, only 1 form allow
    && <Button disabled={buttonDisable} onClick={handleAddRow} size="sm">{addRowLabel}</Button>}
  </>
}

const DND_ITEM_TYPE = 'row'

const TableRow = ({ row, index, moveRow, onMoveRowFinished, draggable }) => {
  const dropRef = React.useRef(null)
  const dragRef = React.useRef(null)

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    drop: () => { onMoveRowFinished() },
    hover(item, monitor) {
      if (!dropRef.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: DND_ITEM_TYPE, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1

  preview(drop(dropRef))
  drag(dragRef)

  return (
    <tr ref={dropRef} style={{ opacity }}>
      {draggable && <td ref={dragRef} style={{ width: '40px' }}>
        <DragHandle dragRef={dragRef} />
      </td>}
      {row.cells.map(cell => {
        const {key, ...rest} = cell.getCellProps();
        return <td key={key} {...rest} style={{ verticalAlign: 'middle' }}>{cell.render('Cell')}</td>
      })}
    </tr>
  )
}

export default EditableList;